import React, { useState, useEffect, useRef } from 'react';
import { motion, AnimatePresence } from 'framer-motion';

const avatars = [
  {
    id: 1,
    name: 'Rama',
    story: 'Rama, the prince of Ayodhya, defeated the demon king Ravana to rescue his wife Sita.',
    image: '/images/avatars/rama.png'
  },
  {
    id: 2,
    name: 'Krishna',
    story: 'Krishna, the divine charioteer, guided Arjuna in the battle of Kurukshetra with spiritual wisdom.',
    image: '/images/avatars/krishna.png'
  },
  {
    id: 3,
    name: 'Matsya',
    story: 'Matsya, the fish avatar, saved humanity and the Vedas from a great deluge.',
    image: '/images/avatars/matsya.png'
  },
  {
    id: 4,
    name: 'Narasimha',
    story: 'Narasimha protected Prahlad from his father\'s cruelty by appearing as half-man, half-lion.',
    image: '/images/avatars/narasimha.png'
  },
  {
    id: 5,
    name: 'Varaha',
    story: 'Varaha, the boar avatar, rescued the Earth goddess from the depths of the cosmic ocean.',
    image: '/images/avatars/varaha.png'
  },
  {
    id: 6,
    name: 'Vamana',
    story: 'Vamana, the dwarf avatar, reclaimed the heavens from the demon king Bali in three steps.',
    image: '/images/avatars/vamana.png'
  },
  {
    id: 7,
    name: 'Parashurama',
    story: 'Parashurama, the warrior sage, rid the world of corrupt kings wielding a mighty axe.',
    image: '/images/avatars/parashurama.png'
  },
  {
    id: 8,
    name: 'Kurma',
    story: 'Kurma, the tortoise avatar, supported Mount Mandara during the churning of the cosmic ocean.',
    image: '/images/avatars/kurma.png'
  }
];

const AvatarMatchingGame = () => {
  const [cards, setCards] = useState([]);
  const [flipped, setFlipped] = useState([]);
  const [matched, setMatched] = useState([]);
  const [moves, setMoves] = useState(0);
  const [gameCompleted, setGameCompleted] = useState(false);
  const [showStory, setShowStory] = useState(null);
  const audioContextRef = useRef(null);

  // Initialize game
  useEffect(() => {
    initializeGame();
  }, []);

  // Initialize audio context
  const initAudio = () => {
    if (!audioContextRef.current) {
      audioContextRef.current = new (window.AudioContext || window.webkitAudioContext)();
    }
  };

  // Play sound effects using Web Audio API
  const playSound = (type) => {
    initAudio();
    const audioContext = audioContextRef.current;
    
    if (!audioContext) return;
    
    try {
      switch (type) {
        case 'flip':
          // Create a flipping card sound
          const flipOscillator = audioContext.createOscillator();
          const flipGain = audioContext.createGain();
          
          flipOscillator.connect(flipGain);
          flipGain.connect(audioContext.destination);
          
          flipOscillator.type = 'sine';
          flipOscillator.frequency.setValueAtTime(440, audioContext.currentTime); // A4
          flipOscillator.frequency.exponentialRampToValueAtTime(880, audioContext.currentTime + 0.1);
          flipGain.gain.setValueAtTime(0.1, audioContext.currentTime);
          flipGain.gain.exponentialRampToValueAtTime(0.01, audioContext.currentTime + 0.1);
          
          flipOscillator.start(audioContext.currentTime);
          flipOscillator.stop(audioContext.currentTime + 0.1);
          break;
          
        case 'match':
          // Create a pleasant sound for matching cards
          const matchOscillator = audioContext.createOscillator();
          const matchGain = audioContext.createGain();
          
          matchOscillator.connect(matchGain);
          matchGain.connect(audioContext.destination);
          
          matchOscillator.type = 'sine';
          matchOscillator.frequency.setValueAtTime(523.25, audioContext.currentTime); // C5
          matchOscillator.frequency.setValueAtTime(659.25, audioContext.currentTime + 0.1); // E5
          matchOscillator.frequency.setValueAtTime(783.99, audioContext.currentTime + 0.2); // G5
          matchGain.gain.setValueAtTime(0.2, audioContext.currentTime);
          matchGain.gain.exponentialRampToValueAtTime(0.01, audioContext.currentTime + 0.3);
          
          matchOscillator.start(audioContext.currentTime);
          matchOscillator.stop(audioContext.currentTime + 0.3);
          break;
          
        case 'mismatch':
          // Create a sound for mismatched cards
          const mismatchOscillator = audioContext.createOscillator();
          const mismatchGain = audioContext.createGain();
          
          mismatchOscillator.connect(mismatchGain);
          mismatchGain.connect(audioContext.destination);
          
          mismatchOscillator.type = 'square';
          mismatchOscillator.frequency.setValueAtTime(220, audioContext.currentTime); // A3
          mismatchGain.gain.setValueAtTime(0.2, audioContext.currentTime);
          mismatchGain.gain.exponentialRampToValueAtTime(0.01, audioContext.currentTime + 0.3);
          
          mismatchOscillator.start(audioContext.currentTime);
          mismatchOscillator.stop(audioContext.currentTime + 0.3);
          break;
          
        case 'gameComplete':
          // Create a celebratory sound for game completion
          // Play a simple melody
          const melody = [523.25, 659.25, 783.99, 1046.50]; // C5, E5, G5, C6
          
          melody.forEach((freq, index) => {
            const osc = audioContext.createOscillator();
            const gain = audioContext.createGain();
            
            osc.connect(gain);
            gain.connect(audioContext.destination);
            
            osc.type = 'sine';
            osc.frequency.setValueAtTime(freq, audioContext.currentTime + index * 0.15);
            gain.gain.setValueAtTime(0.2, audioContext.currentTime + index * 0.15);
            gain.gain.exponentialRampToValueAtTime(0.01, audioContext.currentTime + index * 0.15 + 0.1);
            
            osc.start(audioContext.currentTime + index * 0.15);
            osc.stop(audioContext.currentTime + index * 0.15 + 0.1);
          });
          break;
          
        case 'story':
          // Create a gentle sound for story display
          const storyOscillator = audioContext.createOscillator();
          const storyGain = audioContext.createGain();
          
          storyOscillator.connect(storyGain);
          storyGain.connect(audioContext.destination);
          
          storyOscillator.type = 'sine';
          storyOscillator.frequency.setValueAtTime(392, audioContext.currentTime); // G4
          storyOscillator.frequency.exponentialRampToValueAtTime(523.25, audioContext.currentTime + 0.5); // C5
          storyGain.gain.setValueAtTime(0.15, audioContext.currentTime);
          storyGain.gain.exponentialRampToValueAtTime(0.01, audioContext.currentTime + 0.5);
          
          storyOscillator.start(audioContext.currentTime);
          storyOscillator.stop(audioContext.currentTime + 0.5);
          break;
          
        default:
          break;
      }
    } catch (error) {
      console.log('Sound error:', error);
    }
  };

  const initializeGame = () => {
    // Create pairs of cards
    const pairedAvatars = [...avatars, ...avatars];
    // Shuffle cards
    const shuffled = [...pairedAvatars].sort(() => Math.random() - 0.5);
    setCards(shuffled.map((avatar, index) => ({
      ...avatar,
      id: `${avatar.id}-${index}`
    })));
    setFlipped([]);
    setMatched([]);
    setMoves(0);
    setGameCompleted(false);
    setShowStory(null);
  };

  const handleCardClick = (clickedCard) => {
    // Don't process if card is already flipped or matched
    if (flipped.length === 2 || flipped.includes(clickedCard.id) || matched.includes(clickedCard.id)) {
      return;
    }

    // Play flip sound
    playSound('flip');
    
    const newFlipped = [...flipped, clickedCard.id];
    setFlipped(newFlipped);

    // Check for match when two cards are flipped
    if (newFlipped.length === 2) {
      setMoves(moves + 1);
      const firstCard = cards.find(card => card.id === newFlipped[0]);
      const secondCard = cards.find(card => card.id === newFlipped[1]);

      if (firstCard.name === secondCard.name) {
        // Match found
        playSound('match');
        setMatched([...matched, firstCard.id, secondCard.id]);
        setShowStory(firstCard);
        playSound('story');
        
        // Check if game is completed
        if (matched.length + 2 === cards.length) {
          setTimeout(() => {
            setGameCompleted(true);
            playSound('gameComplete');
          }, 1000);
        }
        
        // Reset flipped after showing story
        setTimeout(() => {
          setFlipped([]);
          setShowStory(null);
        }, 3000);
      } else {
        // No match, flip back after delay
        playSound('mismatch');
        setTimeout(() => {
          setFlipped([]);
        }, 1000);
      }
    }
  };

  // Check if a card is flipped or matched
  const isFlipped = (cardId) => flipped.includes(cardId) || matched.includes(cardId);

  return (
    <div className="max-w-6xl mx-auto px-4 py-8">
      <h1 className="text-4xl font-bold text-center mb-2 text-red-500">Match the Avatars</h1>
      <p className="text-center text-lg mb-8 text-gray-600">Flip cards to find matching avatars of Lord Vishnu!</p>
      
      <div className="flex justify-center items-center mb-6">
        <div className="bg-yellow-100 rounded-full px-6 py-2 shadow-md">
          <span className="font-bold text-red-500">Moves: {moves}</span>
        </div>
      </div>

      {gameCompleted && (
        <motion.div 
          initial={{ scale: 0.8, opacity: 0 }}
          animate={{ scale: 1, opacity: 1 }}
          className="bg-gradient-to-r from-red-400 to-red-600 rounded-xl p-6 text-white text-center mb-8"
        >
          <h2 className="text-3xl font-bold mb-2">Congratulations!</h2>
          <p className="text-xl mb-4">You completed the game in {moves} moves!</p>
          <button 
            onClick={initializeGame}
            className="bg-white text-red-600 font-bold py-2 px-6 rounded-full hover:bg-yellow-200 transition-colors"
          >
            Play Again
          </button>
        </motion.div>
      )}

      <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-6 gap-4">
        {cards.map((card) => (
          <motion.div
            key={card.id}
            className="aspect-square cursor-pointer"
            onClick={() => handleCardClick(card)}
            whileHover={{ scale: 1.05 }}
            whileTap={{ scale: 0.95 }}
          >
            <div className="relative w-full h-full">
              {/* Front of card (question mark) */}
              <motion.div
                className="absolute inset-0 bg-gradient-to-br from-red-50 to-red-400 rounded-xl shadow-lg flex items-center justify-center"
                style={{ 
                  backfaceVisibility: 'hidden',
                  transformStyle: 'preserve-3d'
                }}
                animate={{ 
                  rotateY: isFlipped(card.id) ? 180 : 0
                }}
                transition={{ duration: 0.3 }}
              >
                <span className="text-white text-4xl">?</span>
              </motion.div>
              
              {/* Back of card (avatar image) */}
              <motion.div
                className="absolute inset-0 bg-white rounded-xl shadow-lg overflow-hidden"
                style={{ 
                  backfaceVisibility: 'hidden',
                  transformStyle: 'preserve-3d',
                  transform: 'rotateY(180deg)'
                }}
                animate={{ 
                  rotateY: isFlipped(card.id) ? 360 : 180
                }}
                transition={{ duration: 0.3 }}
              >
                <img 
                  src={card.image} 
                  alt={card.name} 
                  className="w-full h-full object-cover"
                  onError={(e) => {
                    e.target.src = '/images/placeholder-avatar.jpg';
                  }}
                />
                <div className="absolute bottom-0 left-0 right-0 bg-gradient-to-t from-black to-transparent p-2">
                  <h3 className="text-white font-bold text-center">{card.name}</h3>
                </div>
              </motion.div>
            </div>
          </motion.div>
        ))}
      </div>

      {/* Story display */}
      <AnimatePresence>
        {showStory && (
          <motion.div
            initial={{ opacity: 0, y: 50 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: -50 }}
            className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-4 z-50"
            onClick={() => setShowStory(null)}
          >
            <motion.div
              initial={{ scale: 0.8 }}
              animate={{ scale: 1 }}
              className="bg-white rounded-2xl p-6 max-w-md w-full"
              onClick={(e) => e.stopPropagation()}
            >
              <div className="flex justify-between items-start mb-4">
                <h3 className="text-2xl font-bold text-red-600">{showStory.name}</h3>
                <button 
                  onClick={() => setShowStory(null)}
                  className="text-gray-500 hover:text-gray-700"
                >
                  ✕
                </button>
              </div>
              <div className="mb-4 flex items-center justify-center">
                <img 
                  src={showStory.image} 
                  alt={showStory.name} 
                  className="max-h-60 w-auto object-contain rounded-lg"
                  onError={(e) => {
                    e.target.src = '/images/placeholder-avatar.jpg';
                  }}
                />
              </div>
              <p className="text-gray-700">{showStory.story}</p>
              <div className="mt-4 flex justify-center">
                <button
                  onClick={() => setShowStory(null)}
                  className="bg-red-600 text-white px-4 py-2 rounded-full hover:bg-red-700 transition-colors"
                >
                  Continue Playing
                </button>
              </div>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>

      <div className="mt-8 text-center">
        <button 
          onClick={initializeGame}
          className="bg-red-600 text-white font-bold py-3 px-8 rounded-full hover:bg-red-700 transition-colors shadow-lg"
        >
          Restart Game
        </button>
      </div>
    </div>
  );
};

export default AvatarMatchingGame;